import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ViewKanbanRoundedIcon from "@mui/icons-material/ViewKanbanRounded";
import ContactsRoundedIcon from "@mui/icons-material/ContactsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import XIcon from "@mui/icons-material/X";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import { ScheduleDemoDialog } from "../../Components/ScheduleDemo";
import RequestForm from "./requestForm";
import { newLead } from "../../Action/api";

const palette = {
  bg: "#FFFFFF",
  surface: "#F5F7FF",
  ink: "#0B1220",
  inkSoft: "#5B6478",
  border: "#ECEFFA",
  accent: "#4F46E5",
  accent2: "#06B6D4",
  accentSoft: "#EEF0FF",
  good: "#16A672",
  gradient: "linear-gradient(135deg, #4F46E5 0%, #06B6D4 100%)",
};

const cardShadow = "0 12px 32px rgba(17, 24, 39, 0.06)";
const cardShadowHover = `0 24px 48px ${alpha(palette.accent, 0.18)}`;

const proofStats = [
  { value: "4.2x", label: "Faster lead-to-close" },
  { value: "68%", label: "Less manual admin" },
  { value: "99.95%", label: "Platform uptime" },
  { value: "2 weeks", label: "Average go-live" },
];

const trustedLogos = ["Northforge", "Vectra Grid", "Lumen Bay", "Crestline", "Atlas Ops", "Harbor & Co"];

function StatCounter({ value, sx }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(null);

  useEffect(() => {
    const match = value.match(/^([\d.]+)(.*)$/);
    if (!match) return;
    const target = parseFloat(match[1]);
    const suffix = match[2];
    const decimals = match[1].includes(".") ? match[1].split(".")[1].length : 0;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        const duration = 1100;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          setDisplay(`${(target * progress).toFixed(decimals)}${suffix}`);
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [value]);

  return (
    <Typography ref={ref} sx={sx}>
      {display ?? value}
    </Typography>
  );
}

const pillars = [
  {
    icon: <AutoAwesomeRoundedIcon />,
    title: "Intent Engine",
    desc: "Detect buying signals across calls, site behavior, and campaigns before your team misses the window.",
  },
  {
    icon: <ViewInArRoundedIcon />,
    title: "Live Revenue Map",
    desc: "See pipeline health as a living canvas, not another dead spreadsheet dashboard.",
  },
  {
    icon: <BoltRoundedIcon />,
    title: "Zero-Friction Ops",
    desc: "Automate routing, follow-ups, approvals, and reminders with auditable logic in minutes.",
  },
  {
    icon: <SecurityRoundedIcon />,
    title: "Enterprise Ready",
    desc: "Granular permissions, audit timelines, and secure integrations designed for scale.",
  },
];

function HeroDashboardGraphic() {
  const navIcons = [AutoAwesomeRoundedIcon, ViewKanbanRoundedIcon, CalendarMonthRoundedIcon, ContactsRoundedIcon, InsightsRoundedIcon];
  const miniStats = [
    ["Forecast Accuracy", "+37%"],
    ["Avg Deal Cycle", "-9 days"],
    ["Rep Productivity", "+44%"],
  ];
  return (
    <Box sx={{ borderRadius: 3, overflow: "hidden", border: `1px solid ${palette.border}`, bgcolor: palette.bg }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.9,
          px: 1.8,
          py: 1.1,
          bgcolor: palette.surface,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        {["#FF6B5D", "#FFC93C", "#16A672"].map((c) => (
          <Box key={c} sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: c }} />
        ))}
        <Box sx={{ ml: 1.2, height: 16, flex: 1, maxWidth: 220, borderRadius: 1, bgcolor: alpha(palette.ink, 0.06) }} />
      </Box>

      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            width: 48,
            bgcolor: palette.ink,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.6,
            py: 2.2,
          }}
        >
          {navIcons.map((Icon, i) => (
            <Box
              key={i}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.4,
                display: "grid",
                placeItems: "center",
                color: i === 1 ? "white" : alpha("#FFFFFF", 0.45),
                bgcolor: i === 1 ? alpha(palette.accent, 0.7) : "transparent",
              }}
            >
              <Icon sx={{ fontSize: 16 }} />
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1.4 }}>
          <Stack direction="row" spacing={1}>
            {miniStats.map(([k, v]) => (
              <Box
                key={k}
                sx={{
                  flex: 1,
                  p: 1.1,
                  borderRadius: 1.6,
                  bgcolor: palette.surface,
                  border: `1px solid ${palette.border}`,
                }}
              >
                <Typography sx={{ fontSize: "0.6rem", color: palette.inkSoft }}>{k}</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: "0.8rem", color: palette.accent }}>{v}</Typography>
              </Box>
            ))}
          </Stack>

          <Box sx={{ flex: 1, borderRadius: 1.6, border: `1px solid ${palette.border}`, minHeight: 92 }}>
            <PipelineGraphic />
          </Box>

          <Box sx={{ height: 70, borderRadius: 1.6, border: `1px solid ${palette.border}`, bgcolor: palette.surface }}>
            <ChartGraphic />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FunnelGraphic() {
  const bars = [100, 78, 56, 34];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, justifyContent: "center", height: "100%", px: 3 }}>
      {bars.map((w, i) => (
        <Box
          key={w}
          sx={{
            height: 12,
            width: `${w}%`,
            borderRadius: 5,
            background: palette.gradient,
            opacity: 1 - i * 0.16,
            transformOrigin: "left",
            animation: `funnelGrow 2.6s ease-in-out ${i * 0.15}s infinite alternate`,
            "@keyframes funnelGrow": {
              "0%": { transform: "scaleX(0.82)" },
              "100%": { transform: "scaleX(1)" },
            },
          }}
        />
      ))}
    </Box>
  );
}

function PipelineGraphic() {
  const cols = [
    { label: "New", items: 2 },
    { label: "Qualified", items: 3, active: true },
    { label: "Won", items: 1 },
  ];
  return (
    <Box sx={{ display: "flex", gap: 1.4, height: "100%", px: 2.6, pt: 2.4 }}>
      {cols.map((c) => (
        <Box key={c.label} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.8 }}>
          <Typography sx={{ fontSize: "0.62rem", color: palette.inkSoft, fontWeight: 800 }}>{c.label}</Typography>
          {Array.from({ length: c.items }).map((_, i) => (
            <Box
              key={i}
              sx={{
                height: 18,
                borderRadius: 1.4,
                bgcolor: c.active && i === 0 ? palette.accent : palette.bg,
                border: `1px solid ${palette.border}`,
                boxShadow: c.active && i === 0 ? `0 8px 16px ${alpha(palette.accent, 0.4)}` : "none",
                animation: c.active && i === 0 ? "pipelineMove 2.4s ease-in-out infinite" : "none",
                "@keyframes pipelineMove": {
                  "0%, 100%": { transform: "translateX(0px)" },
                  "50%": { transform: "translateX(8px)" },
                },
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

function CalendarGraphic() {
  const cells = Array.from({ length: 21 });
  const highlighted = 12;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: 0.7,
        px: 3,
        alignContent: "center",
        height: "100%",
      }}
    >
      {cells.map((_, i) => (
        <Box
          key={i}
          sx={{
            width: 13,
            height: 13,
            borderRadius: 1,
            mx: "auto",
            bgcolor: i === highlighted ? palette.accent : palette.border,
            animation: i === highlighted ? "calPulse 2.2s ease-out infinite" : "none",
            "@keyframes calPulse": {
              "0%": { boxShadow: `0 0 0 0 ${alpha(palette.accent, 0.45)}` },
              "70%": { boxShadow: `0 0 0 7px ${alpha(palette.accent, 0)}` },
              "100%": { boxShadow: `0 0 0 0 ${alpha(palette.accent, 0)}` },
            },
          }}
        />
      ))}
    </Box>
  );
}

function ChecklistGraphic() {
  const tasks = [0, 1, 2, 3];
  return (
    <Box sx={{ px: 3, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1.3 }}>
      {tasks.map((i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              border: `2px solid ${palette.accent}`,
              animation: `taskTick 3.2s ease-in-out ${i * 0.5}s infinite`,
              "@keyframes taskTick": {
                "0%, 60%, 100%": { backgroundColor: "transparent" },
                "75%, 90%": { backgroundColor: palette.accent },
              },
            }}
          />
          <Box sx={{ height: 6, flex: 1, borderRadius: 3, bgcolor: palette.border }} />
        </Box>
      ))}
    </Box>
  );
}

function AvatarsGraphic() {
  const people = ["A", "B", "C", "D"];
  const colors = [palette.accent, palette.accent2, "#F59E0B", "#EC4899"];
  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", px: 3 }}>
      {people.map((p, i) => (
        <Box
          key={p}
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: colors[i],
            color: "white",
            fontWeight: 800,
            fontSize: "0.85rem",
            border: "2px solid white",
            ml: i === 0 ? 0 : -1.4,
            boxShadow: "0 4px 10px rgba(0,0,0,0.14)",
            animation: `avatarFloat 2.6s ease-in-out ${i * 0.2}s infinite`,
            "@keyframes avatarFloat": {
              "0%, 100%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-6px)" },
            },
          }}
        >
          {p}
        </Box>
      ))}
    </Box>
  );
}

function ChartGraphic() {
  const bars = [40, 65, 50, 82, 60, 95];
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.2, height: "100%", px: 3, pb: 2.4 }}>
      {bars.map((h, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            borderRadius: 1,
            background: palette.gradient,
            height: `${h}%`,
            transformOrigin: "bottom",
            animation: `chartGrow 2.6s ease-in-out ${i * 0.12}s infinite alternate`,
            "@keyframes chartGrow": {
              "0%": { transform: "scaleY(0.55)" },
              "100%": { transform: "scaleY(1)" },
            },
          }}
        />
      ))}
    </Box>
  );
}

function QuoteGraphic() {
  const lines = [88, 68, 48];
  return (
    <Box sx={{ px: 3, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: 1.1 }}>
      {lines.map((w) => (
        <Box key={w} sx={{ height: 7, width: `${w}%`, borderRadius: 4, bgcolor: palette.border }} />
      ))}
      <Box sx={{ mt: 1.4, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 800, color: palette.ink }}>Total: $12,400</Typography>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            background: palette.gradient,
            color: "white",
            animation: "quotePop 1.8s ease-in-out infinite",
            "@keyframes quotePop": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.2)" },
            },
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
        </Box>
      </Box>
    </Box>
  );
}

function TimelineGraphic() {
  const events = [62, 46, 36, 24];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", px: 3.4, py: 2, justifyContent: "center", gap: 1.3, position: "relative" }}>
      <Box sx={{ position: "absolute", left: 30, top: 18, bottom: 18, width: 2, bgcolor: palette.border }} />
      {events.map((w, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.6, position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: palette.accent,
              animation: `timelinePulse 2.4s ease-in-out ${i * 0.3}s infinite`,
              "@keyframes timelinePulse": {
                "0%, 100%": { opacity: 0.4, transform: "scale(0.85)" },
                "50%": { opacity: 1, transform: "scale(1.15)" },
              },
            }}
          />
          <Box sx={{ height: 6, width: `${w}%`, borderRadius: 3, bgcolor: palette.border }} />
        </Box>
      ))}
    </Box>
  );
}

const crmFeatures = [
  {
    icon: <PersonSearchRoundedIcon />,
    title: "Lead Management",
    desc: "Capture, score, and route leads automatically so nothing sits untouched in an inbox.",
    span: { xs: 12, md: 6 },
    height: { xs: 150, md: 190 },
    Visual: FunnelGraphic,
  },
  {
    icon: <ViewKanbanRoundedIcon />,
    title: "Sales Pipeline",
    desc: "Drag-and-drop kanban from qualified to closed-won, with stage rules built in.",
    span: { xs: 12, sm: 6, md: 3 },
    height: { xs: 150, md: 190 },
    Visual: PipelineGraphic,
  },
  {
    icon: <CalendarMonthRoundedIcon />,
    title: "Calendar & Meetings",
    desc: "Schedule, sync, and prep for meetings without jumping between five different apps.",
    span: { xs: 12, sm: 6, md: 3 },
    height: { xs: 150, md: 190 },
    Visual: CalendarGraphic,
  },
  {
    icon: <TaskAltRoundedIcon />,
    title: "Task Management",
    desc: "Assign owners and due dates, and keep every follow-up accountable across the team.",
    span: { xs: 12, sm: 6, md: 3 },
    height: { xs: 150, md: 190 },
    Visual: ChecklistGraphic,
  },
  {
    icon: <ContactsRoundedIcon />,
    title: "Contacts & Companies",
    desc: "One record per person and organization, with full relationship history attached.",
    span: { xs: 12, sm: 6, md: 3 },
    height: { xs: 150, md: 190 },
    Visual: AvatarsGraphic,
  },
  {
    icon: <InsightsRoundedIcon />,
    title: "Reports & Dashboards",
    desc: "Real-time pipeline, productivity, and revenue insight without exporting a single sheet.",
    span: { xs: 12, md: 6 },
    height: { xs: 150, md: 190 },
    Visual: ChartGraphic,
  },
  {
    icon: <RequestQuoteRoundedIcon />,
    title: "Quotation Builder",
    desc: "Create, send, and track branded quotes without leaving the deal record.",
    span: { xs: 12, md: 6 },
    height: { xs: 150, md: 190 },
    Visual: QuoteGraphic,
  },
  {
    icon: <TimelineRoundedIcon />,
    title: "Activity Timeline",
    desc: "Every call, email, and note logged automatically into a single chronological view.",
    span: { xs: 12, md: 6 },
    height: { xs: 150, md: 190 },
    Visual: TimelineGraphic,
  },
];

const matchups = [
  {
    item: "AI guidance that explains why",
    orucore: "Native",
    other: "Basic prompts",
  },
  {
    item: "Visual workflow builder",
    orucore: "Drag + deploy",
    other: "Complex setup",
  },
  {
    item: "Cross-channel timeline",
    orucore: "Unified",
    other: "Fragmented",
  },
  {
    item: "Onboarding speed",
    orucore: "Days",
    other: "Weeks / months",
  },
];

const reviews = [
  {
    quote:
      "We replaced three disconnected tools and finally got one clean operating rhythm. Our forecast meetings are now 20 minutes, not 2 hours.",
    name: "Nadia Rahman",
    role: "VP Revenue, Northforge",
    avatar: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=240&q=80",
  },
  {
    quote:
      "The interface made adoption effortless. Reps wanted to use it from day one, which never happens with enterprise software.",
    name: "Ethan Cole",
    role: "Head of GTM, Vectra Grid",
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=240&q=80",
  },
];

export const LandingPage = ({ onGetStartedClick }) => {
  const [openScheduleDemo, setOpenScheduleDemo] = useState(false);
  const [openRequestForm, setOpenRequestForm] = useState(false);

  const handleOpenRequestForm = () => setOpenRequestForm(true);
  const handleCloseRequestForm = () => setOpenRequestForm(false);
  const handleOpenScheduleDemo = () => setOpenScheduleDemo(true);
  const handleCloseScheduleDemo = () => setOpenScheduleDemo(false);

  const handleRequestFormSubmit = async (data) => {
    try {
      await newLead(data);
      console.log("Customer enquiry submitted:", data);
    } catch (error) {
      console.error("Failed to submit enquiry:", error);
    }
  };

  const handlePrimaryAction = () => {
    if (onGetStartedClick) {
      onGetStartedClick();
      return;
    }
    handleOpenRequestForm();
  };

  return (
    <Box sx={{ bgcolor: palette.bg, minHeight: "100vh", color: palette.ink, overflowX: "hidden" }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 8, md: 11 },
          pb: { xs: 6, md: 7 },
          background: `
            radial-gradient(900px 480px at 12% 0%, ${alpha(palette.accent, 0.12)} 0%, transparent 60%),
            radial-gradient(800px 460px at 95% 10%, ${alpha(palette.accent2, 0.14)} 0%, transparent 60%),
            #FFFFFF
          `,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 520,
            height: 520,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(palette.accent, 0.1)} 0%, transparent 70%)`,
            right: { xs: -260, md: -160 },
            top: { xs: -100, md: -160 },
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={{ xs: 6, md: 6 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="OruClick CRM // REVENUE OS"
                sx={{
                  mb: 3,
                  fontWeight: 800,
                  letterSpacing: 1,
                  background: palette.gradient,
                  color: "white",
                  boxShadow: `0 10px 24px ${alpha(palette.accent, 0.32)}`,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "2.3rem", sm: "3.2rem", md: "4rem" },
                  fontWeight: 900,
                  lineHeight: 1.06,
                  letterSpacing: "-0.03em",
                  maxWidth: 760,
                }}
              >
                Not another CRM.
                <br />
                <Box
                  component="span"
                  sx={{
                    background: palette.gradient,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  A revenue weapon.
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 3,
                  maxWidth: 600,
                  color: palette.inkSoft,
                  lineHeight: 1.75,
                  fontSize: { xs: "1rem", md: "1.15rem" },
                }}
              >
                If Salesforce feels heavy and HubSpot feels generic, OruClick CRM is your next move.
                High-speed execution, premium UI, AI decisions, and workflows your team actually
                wants to open every morning.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={handlePrimaryAction}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    py: 1.6,
                    px: 3.5,
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: "1rem",
                    background: palette.gradient,
                    boxShadow: `0 16px 34px ${alpha(palette.accent, 0.32)}`,
                    "&:hover": { boxShadow: `0 20px 40px ${alpha(palette.accent, 0.4)}`, transform: "translateY(-2px)" },
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleOpenScheduleDemo}
                  startIcon={<PlayCircleRoundedIcon />}
                  sx={{
                    py: 1.6,
                    px: 3.2,
                    borderRadius: 2.5,
                    textTransform: "none",
                    color: palette.ink,
                    fontWeight: 700,
                    borderColor: palette.border,
                    borderWidth: 1.5,
                    "&:hover": { borderColor: palette.accent, bgcolor: palette.accentSoft, borderWidth: 1.5 },
                  }}
                >
                  Watch 2-Min Demo
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 4,
                  bgcolor: palette.bg,
                  p: 1.6,
                  boxShadow: "0 30px 70px rgba(17, 24, 39, 0.14)",
                  transform: { md: "rotate(1.5deg)" },
                }}
              >
                <HeroDashboardGraphic />

                <Grid container spacing={1.2} sx={{ mt: 1.6 }}>
                  {[
                    ["Follow-up SLA", "99%"],
                    ["Platform Uptime", "99.95%"],
                  ].map(([k, v]) => (
                    <Grid item xs={6} key={k}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2.2,
                          bgcolor: palette.surface,
                          border: `1px solid ${palette.border}`,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.74rem", color: palette.inkSoft }}>{k}</Typography>
                        <Typography sx={{ fontWeight: 900, mt: 0.2, color: palette.accent }}>{v}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 3.5, md: 4.5 }, borderTop: `1px solid ${palette.border}`, borderBottom: `1px solid ${palette.border}` }}>
        <Container maxWidth="lg">
          <Typography
            sx={{
              textAlign: "center",
              color: palette.inkSoft,
              letterSpacing: 1.6,
              fontSize: "0.72rem",
              fontWeight: 800,
              mb: 2,
            }}
          >
            TRUSTED BY REVENUE TEAMS AT
          </Typography>
          <Stack
            direction="row"
            spacing={{ xs: 3, md: 5 }}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
          >
            {trustedLogos.map((name) => (
              <Typography
                key={name}
                sx={{
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  color: alpha(palette.ink, 0.35),
                  letterSpacing: "-0.01em",
                  transition: "color 200ms ease",
                  "&:hover": { color: palette.accent },
                }}
              >
                {name}
              </Typography>
            ))}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 7 } }}>
        <Grid container spacing={2.5}>
          {proofStats.map((s, idx) => (
            <Grid item xs={6} md={3} key={s.label}>
              <Box
                sx={{
                  p: 2.4,
                  borderRadius: 3,
                  bgcolor: palette.bg,
                  boxShadow: cardShadow,
                  borderTop: `3px solid transparent`,
                  borderImage: palette.gradient,
                  borderImageSlice: 1,
                  animation: `riseIn 500ms ease ${idx * 80}ms both`,
                  transition: "transform 220ms ease, box-shadow 220ms ease",
                  "&:hover": { transform: "translateY(-5px)", boxShadow: cardShadowHover },
                  "@keyframes riseIn": {
                    from: { opacity: 0, transform: "translateY(12px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                <StatCounter value={s.value} sx={{ fontSize: { xs: "1.3rem", md: "1.7rem" }, fontWeight: 900 }} />
                <Typography sx={{ mt: 0.4, color: palette.inkSoft, fontSize: "0.86rem" }}>
                  {s.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ py: { xs: 6.5, md: 9 }, bgcolor: palette.surface }}>
        <Container maxWidth="lg">
          <Typography
            sx={{
              color: palette.accent,
              letterSpacing: 1.4,
              fontSize: "0.8rem",
              fontWeight: 800,
              mb: 1.4,
            }}
          >
            PRODUCT DNA
          </Typography>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2rem", md: "2.8rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.12,
              maxWidth: 780,
              mb: 5,
            }}
          >
            Designed to feel premium,
            <br />
            built to move revenue fast.
          </Typography>

          <Grid container spacing={2.5}>
            {pillars.map((p) => (
              <Grid item xs={12} sm={6} md={3} key={p.title}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    bgcolor: palette.bg,
                    border: "none",
                    boxShadow: cardShadow,
                    transition: "transform 220ms ease, box-shadow 220ms ease",
                    "&:hover": { transform: "translateY(-7px)", boxShadow: cardShadowHover },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        background: palette.gradient,
                        color: "white",
                        display: "grid",
                        placeItems: "center",
                        mb: 2,
                        boxShadow: `0 10px 22px ${alpha(palette.accent, 0.3)}`,
                      }}
                    >
                      {p.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>{p.title}</Typography>
                    <Typography sx={{ color: palette.inkSoft, lineHeight: 1.68, fontSize: "0.92rem" }}>
                      {p.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6.5, md: 9 } }}>
        <Container maxWidth="lg">
          <Typography
            sx={{
              color: palette.accent,
              letterSpacing: 1.4,
              fontSize: "0.8rem",
              fontWeight: 800,
              mb: 1.4,
            }}
          >
            CORE CRM FEATURES
          </Typography>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: "2rem", md: "2.8rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.12,
              maxWidth: 780,
              mb: 5,
            }}
          >
            Everything your revenue team
            <br />
            already needs, in one place.
          </Typography>

          <Grid container spacing={2.5}>
            {crmFeatures.map((f) => {
              const Visual = f.Visual;
              return (
                <Grid item {...f.span} key={f.title}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      bgcolor: palette.bg,
                      border: "none",
                      overflow: "hidden",
                      boxShadow: cardShadow,
                      cursor: "pointer",
                      transition: "transform 220ms ease, box-shadow 220ms ease",
                      "&:hover": { transform: "translateY(-7px)", boxShadow: cardShadowHover },
                      "&:hover .feature-explore": { opacity: 1, transform: "translateX(0)" },
                    }}
                  >
                    <Box
                      sx={{
                        height: f.height,
                        bgcolor: palette.surface,
                        borderBottom: `1px solid ${palette.border}`,
                      }}
                    >
                      <Visual />
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1.4}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 1.6,
                            bgcolor: palette.accentSoft,
                            color: palette.accent,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          {f.icon}
                        </Box>
                        <Typography sx={{ fontWeight: 800 }}>{f.title}</Typography>
                      </Stack>
                      <Typography sx={{ color: palette.inkSoft, lineHeight: 1.68, fontSize: "0.92rem", mt: 1.4 }}>
                        {f.desc}
                      </Typography>
                      <Stack
                        className="feature-explore"
                        direction="row"
                        alignItems="center"
                        spacing={0.6}
                        sx={{
                          mt: 1.8,
                          color: palette.accent,
                          fontWeight: 800,
                          fontSize: "0.84rem",
                          opacity: 0,
                          transform: "translateX(-6px)",
                          transition: "opacity 200ms ease, transform 200ms ease",
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: "0.84rem" }}>Explore feature</Typography>
                        <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6.5, md: 9 }, bgcolor: palette.surface }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: palette.accent, fontWeight: 800, letterSpacing: 1.2, mb: 1 }}>
                WHY SWITCH
              </Typography>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1.9rem", md: "2.6rem" },
                  lineHeight: 1.15,
                  mb: 2,
                }}
              >
                Better than legacy CRM.
                <br />
                Cleaner than cookie-cutter CRM.
              </Typography>
              <Typography sx={{ color: palette.inkSoft, lineHeight: 1.75, maxWidth: 560 }}>
                You asked for something more attractive and modern. This is the standard: less clutter,
                bolder hierarchy, clearer decisions, and measurable speed.
              </Typography>

              <Stack spacing={1.1} sx={{ mt: 3 }}>
                {[
                  "No spreadsheet-feel UI",
                  "No hidden complexity",
                  "No stale data silos",
                ].map((item) => (
                  <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleRoundedIcon sx={{ color: palette.good, fontSize: 20 }} />
                    <Typography sx={{ color: palette.ink, fontWeight: 600 }}>{item}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, border: "none", boxShadow: "0 24px 50px rgba(17,34,72,0.1)" }}>
                <CardContent sx={{ p: 0 }}>
                  <Grid container>
                    <Grid item xs={6} sx={{ p: 2.4, background: palette.gradient, color: "white" }}>
                      <Typography sx={{ fontWeight: 900, mb: 0.8 }}>OruClick CRM</Typography>
                      <Typography sx={{ color: alpha("#FFFFFF", 0.84), fontSize: "0.86rem" }}>
                        Built for speed
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ p: 2.4, bgcolor: palette.surface }}>
                      <Typography sx={{ fontWeight: 800, mb: 0.8, color: palette.ink }}>Typical CRM</Typography>
                      <Typography sx={{ color: palette.inkSoft, fontSize: "0.86rem" }}>Built for compromise</Typography>
                    </Grid>
                  </Grid>
                  <Divider />
                  {matchups.map((m) => (
                    <Grid container key={m.item} sx={{ borderBottom: `1px solid ${palette.border}` }}>
                      <Grid item xs={5} sx={{ p: 1.8 }}>
                        <Typography sx={{ color: palette.ink, fontWeight: 700, fontSize: "0.9rem" }}>{m.item}</Typography>
                      </Grid>
                      <Grid item xs={3.5} sx={{ p: 1.8, bgcolor: palette.accentSoft }}>
                        <Typography sx={{ fontWeight: 800, color: palette.accent, fontSize: "0.9rem" }}>{m.orucore}</Typography>
                      </Grid>
                      <Grid item xs={3.5} sx={{ p: 1.8 }}>
                        <Typography sx={{ color: palette.inkSoft, fontSize: "0.9rem" }}>{m.other}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6.5, md: 9 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {reviews.map((r) => (
              <Grid item xs={12} md={6} key={r.name}>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: "none",
                    boxShadow: cardShadow,
                    transition: "transform 220ms ease, box-shadow 220ms ease",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: cardShadowHover },
                  }}
                >
                  <CardContent sx={{ p: 3.2 }}>
                    <Stack direction="row" spacing={0.3} sx={{ mb: 1.6 }}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <StarRoundedIcon key={i} sx={{ color: "#F5A623", fontSize: 18 }} />
                      ))}
                    </Stack>
                    <Typography sx={{ color: palette.ink, lineHeight: 1.8, mb: 2.6, fontSize: "1.02rem" }}>
                      "{r.quote}"
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.3 }}>
                      <Avatar src={r.avatar} alt={r.name} />
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{r.name}</Typography>
                        <Typography sx={{ color: palette.inkSoft, fontSize: "0.85rem" }}>{r.role}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 6.5, md: 9 },
          background: palette.gradient,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(600px 260px at 15% 30%, ${alpha("#FFFFFF", 0.18)} 0%, transparent 70%)`,
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: "2rem", md: "2.9rem" }, lineHeight: 1.1, color: "white" }}>
                If your team is ready for a serious upgrade,
                <br />
                let's build your new revenue rhythm.
              </Typography>
              <Typography sx={{ mt: 1.6, color: alpha("#FFFFFF", 0.88), maxWidth: 700, lineHeight: 1.7 }}>
                You wanted something more attractive. This is your premium version. We can tune this
                even further with your brand voice and market positioning.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.4}>
                <Button
                  variant="contained"
                  onClick={handleOpenRequestForm}
                  startIcon={<RocketLaunchRoundedIcon />}
                  sx={{
                    py: 1.6,
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 2.5,
                    bgcolor: "white",
                    color: palette.accent,
                    boxShadow: "0 14px 30px rgba(0,0,0,0.18)",
                    "&:hover": { bgcolor: "#F2F3FF" },
                  }}
                >
                  Request Personalized Demo
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleOpenScheduleDemo}
                  startIcon={<LanguageRoundedIcon />}
                  sx={{
                    py: 1.6,
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 2.5,
                    color: "white",
                    borderColor: alpha("#FFFFFF", 0.6),
                    borderWidth: 1.5,
                    "&:hover": { borderColor: "white", bgcolor: alpha("#FFFFFF", 0.12), borderWidth: 1.5 },
                  }}
                >
                  Book Strategy Session
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="footer" sx={{ bgcolor: palette.ink, py: 5 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontWeight: 900, fontSize: "1.4rem", letterSpacing: "-0.02em", color: "white" }}>
                Oru<Box component="span" sx={{ color: palette.accent2 }}>core</Box>
              </Typography>
              <Typography sx={{ color: alpha("#FFFFFF", 0.55), mt: 0.7, fontSize: "0.9rem" }}>
                A modern CRM for teams that hate average software.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2.3} justifyContent={{ xs: "flex-start", md: "center" }}>
                <Link href="/privacy" color="inherit" underline="hover" sx={{ color: alpha("#FFFFFF", 0.72) }}>
                  Privacy
                </Link>
                <Link href="/terms" color="inherit" underline="hover" sx={{ color: alpha("#FFFFFF", 0.72) }}>
                  Terms
                </Link>
                <Link href="#" color="inherit" underline="hover" sx={{ color: alpha("#FFFFFF", 0.72) }}>
                  Security
                </Link>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1.1} justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                {[XIcon, LinkedInIcon, InstagramIcon].map((Icon, idx) => (
                  <IconButton
                    key={idx}
                    sx={{
                      color: "white",
                      border: `1px solid ${alpha("#FFFFFF", 0.22)}`,
                      "&:hover": { bgcolor: alpha("#FFFFFF", 0.08) },
                    }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                ))}
              </Stack>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: alpha("#FFFFFF", 0.1) }} />
          <Typography sx={{ color: alpha("#FFFFFF", 0.48), fontSize: "0.85rem" }}>
            © {new Date().getFullYear()} OruClick. All rights reserved.
          </Typography>
        </Container>
      </Box>

      <ScheduleDemoDialog
        open={openScheduleDemo}
        onClose={handleCloseScheduleDemo}
        primaryColor={palette.accent}
      />
      <RequestForm
        open={openRequestForm}
        onClose={handleCloseRequestForm}
        onSubmit={handleRequestFormSubmit}
      />
    </Box>
  );
};

export default LandingPage;
